import { useEffect, useLayoutEffect, useRef, useSyncExternalStore, useState, type MouseEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { AnimatedButton } from './AnimatedButton';
import ThemeToggle from './ThemeToggle';
import { navigationConfig as defaultNavigationConfig } from '@/config';
import { useConfig } from '@/lib/siteData';
import { trackEvent } from '@/lib/plausible';
import { useLiveTarget } from '@/lib/liveTarget';
import { isServicePagePath } from '@/content/servicePages';

// Nav items come from admin-editable `nav_config.links`. Footer link destinations
// match these routes so editors never have to reconcile "navbar anchor vs footer
// path" drift. Home is a soft scroll-to-top when already on `/`.
type NavItem = {
  label: string;
  href: string;
};

/** Hrefs whose availability depends on DB state, so they need the live check.
 *  Everything else renders immediately rather than waiting on a fetch. */
function isGatedHref(href: string): boolean {
  return isServicePagePath(href.split(/[?#]/)[0]);
}

const SCROLL_THRESHOLD = 4;

export function Navigation() {
  const navigationConfig = useConfig('nav_config', defaultNavigationConfig);
  const { isLive, isLoading: targetsLoading } = useLiveTarget();
  const configuredItems: NavItem[] = (navigationConfig.links ?? defaultNavigationConfig.links ?? []) as NavItem[];
  // The nav lists /fractional-cto, which seeds unpublished. Drop any item whose
  // target is not live yet, so the header never advertises a 404 — the owner
  // publishes the page in /admin and the item appears with no redeploy.
  //
  // Hidden WHILE LOADING, deliberately: showing it optimistically would
  // reintroduce the dead link this gate exists to prevent. The cost is a small
  // pop-in on a cold first visit; the localStorage cache covers every repeat.
  const navItems: NavItem[] = targetsLoading
    ? configuredItems.filter((item) => !isGatedHref(item.href))
    : configuredItems.filter((item) => isLive(item.href));
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // useSyncExternalStore: subscribe to scroll events without setState-in-effect.
  // React re-calls getSnapshot on every re-render (including route changes), so
  // the pathname-change re-evaluation is handled automatically.
  const isScrolled = useSyncExternalStore(
    (cb) => {
      window.addEventListener('scroll', cb, { passive: true });
      return () => window.removeEventListener('scroll', cb);
    },
    () => window.scrollY > SCROLL_THRESHOLD,
    () => false,
  );

  // Refs for mobile-menu keyboard accessibility: the overlay container (for the
  // focus trap + focus-into-menu) and the hamburger toggle (to restore focus on
  // close). `wasMenuOpenRef` lets us detect a genuine open→close transition so we
  // don't yank focus onto the toggle on initial mount.
  const overlayRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const wasMenuOpenRef = useRef(false);

  // Standard focusable selector for the trap. Hidden/disabled elements are
  // filtered out at call time via offsetParent + the `disabled` selector.
  const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])';

  const getFocusable = (): HTMLElement[] => {
    const container = overlayRef.current;
    if (!container) return [];
    return Array.from(
      container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    ).filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null);
  };

  // Escape-to-close + Tab/Shift+Tab focus trap, gated on `isMenuOpen`.
  useEffect(() => {
    if (!isMenuOpen) return;
    if (typeof document === 'undefined') return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setIsMenuOpen(false);
        return;
      }
      if (e.key === 'Tab') {
        const focusable = getFocusable();
        if (focusable.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const activeEl = document.activeElement as HTMLElement | null;
        if (e.shiftKey) {
          if (activeEl === first || !overlayRef.current?.contains(activeEl)) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (activeEl === last || !overlayRef.current?.contains(activeEl)) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMenuOpen]);

  // Best-effort: move focus into the menu on open (APG dialog pattern), via
  // useLayoutEffect so it runs after the overlay's `visible` class commits. Note
  // the focus trap below is the real guarantee — the first Tab pulls focus into
  // the menu and cycles within it regardless of whether this initial focus lands.
  useLayoutEffect(() => {
    if (typeof document === 'undefined' || !isMenuOpen) return;
    const focusable = getFocusable();
    (focusable[0] ?? overlayRef.current)?.focus();
  }, [isMenuOpen]);

  // Body scroll lock while open + restore focus to the toggle on a real
  // open→close transition (not on initial mount).
  useEffect(() => {
    if (typeof document === 'undefined') return;

    if (isMenuOpen) {
      wasMenuOpenRef.current = true;
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = previousOverflow;
      };
    }

    if (wasMenuOpenRef.current) {
      wasMenuOpenRef.current = false;
      toggleRef.current?.focus();
    }
  }, [isMenuOpen]);

  if (!navigationConfig.logo && navItems.length === 0) return null;

  // The header CTA is the single most-clicked link on the site and pointed at a
  // bare /contact, recording nothing. It now reads nav_config.contactHref so the
  // `?offer=` lands in leads.cta_id and the destination stays admin-editable.
  //
  // (That key was previously deleted on every boot by a stripper that declared
  // it dead schema — retired in this commit. See migrateAuditShapeOnce HISTORY.)
  const contactHref = navigationConfig.contactHref
    || defaultNavigationConfig.contactHref
    || '/contact';

  const isHome = location.pathname === '/';
  // The hero is now theme-aware (cream/charcoal in light, near-black/white in
  // dark). When the transparent navbar sits over it, we want its text to read
  // strong against whichever hero background is showing — i.e. follow
  // --hero-text-strong via the text-hero-* token. `onHero` is true only at the
  // top of `/`; elsewhere the navbar gets its own bg + standard text tokens.
  const onHero = isHome && !isScrolled;

  const isItemActive = (item: NavItem): boolean => {
    const path = location.pathname;
    if (item.href === '/') return path === '/';
    return path === item.href || path.startsWith(item.href + '/');
  };

  const renderDesktopLink = (item: NavItem) => {
    const active = isItemActive(item);
    // Over the hero (transparent navbar): follow the hero token so dark/light
    // both read strongly against whichever hero background is showing.
    // Elsewhere: the navbar has its own bg → use the standard text tokens.
    const linkColor = onHero
      ? active
        ? 'text-hero-strong font-medium'
        : 'text-hero-soft hover:text-hero-strong'
      : active
        ? 'text-text-primary font-medium'
        : 'text-text-secondary hover:text-text-primary';
    const className = cn('whitespace-nowrap text-sm 2xl:text-base transition-colors duration-300 relative group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue', linkColor);
    const underline = (
      <span
        className={cn(
          'absolute -bottom-1 left-0 h-px transition-all duration-300',
          onHero ? 'bg-hero-strong' : 'bg-text-primary',
          active ? 'w-full' : 'w-0 group-hover:w-full'
        )}
      />
    );

    const handleLinkClick = (e: MouseEvent<HTMLAnchorElement>) => {
      if (item.href === '/' && location.pathname === '/') {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    return (
      <Link
        key={item.label}
        to={item.href}
        onClick={handleLinkClick}
        className={className}
        aria-current={active ? 'page' : undefined}
      >
        {item.label}
        {underline}
      </Link>
    );
  };

  const renderMobileLink = (item: NavItem, index: number) => {
    const active = isItemActive(item);
    const className = cn(
      'text-3xl font-semibold transition-all duration-500 ease-out-quart focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue',
      active ? 'text-accent-blue' : 'text-text-primary',
      isMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
    );
    const style = { transitionDelay: isMenuOpen ? `${index * 100}ms` : '0ms' };

    const handleLinkClick = (e: MouseEvent<HTMLAnchorElement>) => {
      if (item.href === '/' && location.pathname === '/') {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      setIsMenuOpen(false);
    };

    return (
      <Link
        key={item.label}
        to={item.href}
        onClick={handleLinkClick}
        className={className}
        style={style}
        aria-current={active ? 'page' : undefined}
      >
        {item.label}
      </Link>
    );
  };

  return (
    <>
      <nav
        className={cn(
          'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
          // Fully transparent at page top (any page). As soon as the user starts scrolling
          // the nav turns solid. Was bg-[#faf7f0]/95 dark:bg-[#1a1612]/90 -- hand-picked
          // literals, and the dark one was a warm brown-black against a cool blue-black
          // page. It now follows the surface token: by the time the nav is solid the hero
          // has been scrolled past, so matching the page beats matching the hero cream.
          // the nav turns solid. When scrollY returns to 0 it goes transparent again.
          isScrolled
            ? 'bg-surface-elevated/95 dark:bg-surface-elevated/90 backdrop-blur-xl backdrop-saturate-150 border-b border-surface-border-gray/60 shadow-sm'
            : 'bg-transparent border-b border-transparent'
        )}
      >
        <div className="w-full px-6 xl:px-8 2xl:px-12 h-[4.5rem] flex items-center">
          <div className="flex w-full items-center justify-between">
            {/* Logo — always routes home */}
            {navigationConfig.logo && (
              <Link to="/" className="flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue" aria-label="Chinedu Koggu — home">
                <span
                  className={cn(
                    'text-2xl font-semibold tracking-tight transition-colors duration-300',
                    onHero ? 'text-hero-strong' : 'text-text-primary'
                  )}
                >
                  {navigationConfig.logo}
                </span>
              </Link>
            )}

            {/* Desktop Navigation.
                Width budget: nav items come from admin-editable nav_config, so
                folding "Fractional CTO" under Advisory Services can't be done in
                code without fighting the DB value — instead the row is sized to
                fit all 8 default items at >=1280px: text-sm + gap-4 at xl,
                relaxing to text-base + gap-8 at 2xl. If more items are added in
                /admin, prefer consolidating there. */}
            <div className="hidden xl:flex items-center gap-4 2xl:gap-8 min-w-0">
              {navItems.map((item) => renderDesktopLink(item))}
            </div>

            {/* Right side — theme toggle + Contact */}
            <div className="hidden xl:flex items-center gap-3 flex-shrink-0">
              <ThemeToggle
                className={cn(
                  'transition-colors duration-300',
                  onHero
                    ? 'text-hero-strong hover:text-accent-blue'
                    : 'text-text-primary hover:text-accent-blue'
                )}
              />
              {navigationConfig.contactLabel && (
                <AnimatedButton
                  to={contactHref}
                  onClick={() => trackEvent('Nav Contact Click', { location: 'nav' })}
                  variant="primary"
                  size="md"
                  className={
                    onHero
                      ? 'bg-hero-cta-primary-bg text-hero-cta-primary-fg hover:bg-hero-cta-primary-hover'
                      : undefined
                  }
                >
                  {navigationConfig.contactLabel}
                </AnimatedButton>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              ref={toggleRef}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="xl:hidden relative w-8 h-6 flex flex-col justify-between focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-blue focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--color-bg)]"
              aria-label="Toggle menu"
              aria-expanded={isMenuOpen}
              aria-controls="mobile-nav-menu"
            >
              <span
                className={cn(
                  'w-full h-0.5 transition-all duration-500 ease-out-quad origin-center',
                  onHero ? 'bg-hero-strong' : 'bg-text-primary',
                  isMenuOpen && 'translate-y-[10px] rotate-[-45deg]'
                )}
              />
              <span
                className={cn(
                  'w-full h-0.5 transition-all duration-300 ease-out-quad',
                  onHero ? 'bg-hero-strong' : 'bg-text-primary',
                  isMenuOpen && 'scale-0 opacity-0'
                )}
              />
              <span
                className={cn(
                  'w-full h-0.5 transition-all duration-500 ease-out-quad origin-center',
                  onHero ? 'bg-hero-strong' : 'bg-text-primary',
                  isMenuOpen && '-translate-y-[10px] rotate-[45deg]'
                )}
              />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        id="mobile-nav-menu"
        ref={overlayRef}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation"
        tabIndex={-1}
        onClick={(e) => {
          // Close only when the overlay background itself is tapped. Clicks on the
          // nav links / CTA / ThemeToggle bubble from those elements (target !==
          // currentTarget) and must not be swallowed — those close on their own.
          if (e.target === e.currentTarget) setIsMenuOpen(false);
        }}
        className={cn(
          'fixed inset-0 z-40 bg-surface-elevated transition-all duration-500 ease-out-cubic xl:hidden focus:outline-none',
          isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
        )}
      >
        <div className="absolute top-5 right-6">
          <ThemeToggle />
        </div>
        <div className="flex flex-col items-center justify-center h-full gap-8">
          {navItems.map((item, index) => renderMobileLink(item, index))}
          {navigationConfig.contactLabel && (
            <AnimatedButton
              to={contactHref}
              onClick={() => { setIsMenuOpen(false); trackEvent('Nav Contact Click', { location: 'nav' }); }}
              variant="primary"
              size="lg"
              className={cn(
                'mt-4 transition-all duration-500 ease-out-quart',
                isMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              )}
              style={{ transitionDelay: isMenuOpen ? `${navItems.length * 100}ms` : '0ms' }}
            >
              {navigationConfig.contactLabel}
            </AnimatedButton>
          )}
        </div>
      </div>
    </>
  );
}
